import { useCallback, useEffect, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { LegendKey } from "../../../models/chart_model";
import { getHaryanaDailyPeak, getHaryanaMonthlyPeakDiscom } from "../../../Rest_api/RestApi";
import { H_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import MetricChip from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

let X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const UNITS = ["MW", "MWh", "LU"];

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper function to get month with year
function getMonthWithYear(month: number, year: number): string {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const yearShort = year.toString().slice(-2);
  return `${monthNames[month - 1]}-${yearShort}`;
}

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string): { month: number; year: number } {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [monthStr, yearStr] = monthYearStr.split('-');
  const month = monthNames.indexOf(monthStr) + 1;
  const year = 2000 + parseInt(yearStr);
  return { month, year };
}

interface HaryanaPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  metrics?: DiscomLoadMetrics | null;
  metricsLoading?: boolean;
}

export function HaryanaPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  metrics,
  metricsLoading
}: HaryanaPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  // Track which legends are selected in the chart (empty = all selected)
  const [activeLegends, setActiveLegends] = useState<LegendKey[]>([]);

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const uniqueDateValues = DefaultDates[activeTab]?.trendDateOptions || [];

  const createChartObject = (
    key: string,
    suffix: string,
    displayName: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`.trim(),
      type: selectedChartType.value,
      stackId: selectedChartType.stackId !== undefined ? "peak" : undefined,
    };
  };

  const _handlefetchData = useCallback(async () => {
    const defaultTrendDates = DefaultDates[activeTab]?.defaultTrendDates;

    if (!defaultTrendDates || defaultTrendDates.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const promises = defaultTrendDates.map(async (date: any) => {
        try {
          const [startDate, endDate] = date.value.split(",");

          if (selectedXaxis.key === "month") {
            const monthData = await getHaryanaMonthlyPeakDiscom(startDate, endDate);
            // FIXED: Use getMonthWithYear to include year in the month label
            return monthData.map((item) => ({
              ...item,
              month: getMonthWithYear(item.month, item.year),
            }));
          } else {
            const dailyData = await getHaryanaDailyPeak(startDate, endDate);
            return dailyData.map((item) => ({
              ...item,
            }));
          }
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const flattenedData = results.flat();

      // Deduplicate data based on date (or month+year for monthly view)
      // This prevents duplicates when overlapping date ranges are selected
      const dataMap = new Map<string, any>();
      flattenedData.forEach((item) => {
        const uniqueKey = selectedXaxis.key === "month"
          ? `${item.month}_${item.year}`
          : item.date;
        if (!dataMap.has(uniqueKey)) {
          dataMap.set(uniqueKey, item);
        }
      });
      const newData = Array.from(dataMap.values());

      setData(newData);

      // Set unique dates for filtering
      setUniqueDate(
        Array.from(
          new Set(
            newData
              .map((item) => item.date)
              .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
          )
        )
      );
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, uniqueDateValues, selectedXaxis.key]);

  useEffect(() => {
    if (data.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data;

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date)
      );
    }

    // FIXED: Sort filtered data in ascending order
    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        // For month view, sort by year and month
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);

        // First compare years
        if (aMonthYear.year !== bMonthYear.year) {
          return aMonthYear.year - bMonthYear.year;
        }
        // Then compare months
        return aMonthYear.month - bMonthYear.month;
      } else {
        // For date view, sort by date
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    const convertedData = filteredData.map((item) => {
      const conversionFactor = selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;
      return {
        ...item,
        peak_mw: item.peak_mw * conversionFactor,
        morning_peak_mw: item.morning_peak_mw * conversionFactor,
        evening_peak_mw: item.evening_peak_mw * conversionFactor,
        average_mw: item.average_mw ? item.average_mw * conversionFactor : undefined,
      };
    });

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: ["morning_peak_mw", "peak_mw", "evening_peak_mw"],
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedXaxis, selectedUnit, selectedDate]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function getLegends() {
    const suffixes = [
      { suffix: "morning_peak_mw", displayName: "Morning Peak" },
      { suffix: "peak_mw", displayName: "Day Peak" },
      { suffix: "evening_peak_mw", displayName: "Evening Peak" },
    ];

    return suffixes.flatMap(({ suffix, displayName }) =>
      chartKeys.map((key) =>
        createChartObject(key, suffix, displayName)
      )
    );
  }

  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels = selectedXaxis.key === "date"
    ? ["Date"]
    : ["Month", "Year"];

  const xDataKey = selectedXaxis.values[0];

  // For summary/table: use only the selected (active) legends, or all if none selected
  const allLegendsForTable = getLegends() as any[];
  const visibleLegends =
    activeLegends.length > 0
      ? allLegendsForTable.filter((l) =>
        activeLegends.some((al: any) => al.dataKey === l.dataKey)
      )
      : allLegendsForTable;

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Haryana Load Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => onUnitChange(item)}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />

          {/* Disabled Filters */}
          <FilterMenu
            title="Discom"
            items={["UHBVN", "DHBVN"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Group By"
            items={["Discom Load", "Total Discom Load", "HVPN Drawal"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Type"
            items={["Schedule", "Drawal", "Load Shedding", "Total Load"]}
            selectedItems={[]}
            disabled={true}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
         <div className="flex flex-wrap gap-4 my-0 justify-center">
                    <MetricChip
                      icon={<PeakIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Peak Load",
                          value: metrics?.["Peak Load (MW)"]?.value ?? undefined,
                          unit: metrics?.["Peak Load (MW)"]?.unit ?? "",
                        },
                      ]}
                    />
                    <MetricChip
                      icon={<AvgIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Average Load",
                          value: metrics?.["Average Load (MW)"]?.value ?? undefined,
                          unit: metrics?.["Average Load (MW)"]?.unit ?? "",
                        },
                      ]}
                    />
                    <MetricChip
                      icon={<MinIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Minimum Load",
                          value: metrics?.["Minimum Load (MW)"]?.value ?? undefined,
                          unit: metrics?.["Minimum Load (MW)"]?.unit ?? "",
                        },
                      ]}
                    />
                    <MetricChip
                      icon={<TotalIcon width={20} height={19} />}
                      items={[
                        {
                          label: "Total Energy",
                          value: metrics?.["Total Energy (MU)"]?.value ?? undefined,
                          unit: metrics?.["Total Energy (MU)"]?.unit ?? "",
                        },
                      ]}
                    />
                  </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">Haryana Load Peak Analysis ({selectedUnit})</h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(commonSummaryProps);

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(commonSummaryProps);

                      dataTab.label === "Table" &&
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
             

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={getLegends() as any}
                  customLegendOrder={getLegends().map((l: any) => l.name)}
                  xDataKey={xDataKey}
                  secondXDataKey={undefined}
                  defaultSelectedLegends={activeLegends}
                  onLegendSelect={(selected: LegendKey[]) => setActiveLegends(selected)}
                />
              )}
              {dataTab.label === "Summary" && <LegendSummaryTable {...commonSummaryProps} />}
              {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>HPPC</p>}
              text=" "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}